import { Flex, Typography } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { BeatLoader } from 'react-spinners';
import styled from 'styled-components';
import { BluePrimary, Gray7 } from '../../../styles';
import { chatItemType } from '../../../types';
import { GenericTool, TOOL_RENDERERS } from './ToolRenderers/index';
import MarkdownWithAssetLinks from './components/MarkdownWithAssetLinks';
import ThinkingBlock from './components/ThinkingBlock';
import TypewriterText from './components/TypewriterText';

const { Text } = Typography;

const WorkingText = styled(Text)`
  color: ${Gray7};
  font-size: 13px;
`;

// Render individual item (text or tool)
function RenderItem({ item, onTypewriterUpdate = null }) {
  const itemType = item.type;

  if (itemType === 'text') {
    const textContent = item.text || '';
    // Only use typewriter if text is available and it's a new message
    const shouldTypewrite =
      item.role !== 'user' && item.isNewMessage === true && textContent.length > 0;

    return (
      <div>
        {shouldTypewrite ? (
          <TypewriterText onUpdate={onTypewriterUpdate}>{textContent}</TypewriterText>
        ) : (
          <MarkdownWithAssetLinks>{textContent}</MarkdownWithAssetLinks>
        )}
      </div>
    );
  }

  if (itemType === 'tool') {
    const toolName = item.name;

    // Render the appropriate tool component (handles pending state internally)
    const ToolRenderer = TOOL_RENDERERS[toolName] || GenericTool;
    return <ToolRenderer item={item} />;
  }

  return null;
}

RenderItem.propTypes = {
  item: chatItemType.isRequired,
  onTypewriterUpdate: PropTypes.func,
};

function MessageItem({ items, loading, onTypewriterUpdate = null }) {
  // For agent messages (both ai-analyst and deep-researcher), use ThinkingBlock pattern
  if (items.length > 0 || loading) {
    // Determine if an item should be shown outside the ThinkingBlock
    const isOutsideItem = (item) => {
      const mainRoles = ['user', 'co_analyst', 'deep_researcher', 'report_composer'];
      const outsideTools = [
        'display_chart',
        'display_cached_data',
        'display_verbatim',
        'display_segment_insights',
      ];

      // Only show text items outside if the role is one of the main roles
      if (item.type === 'text' && mainRoles.includes(item.role)) {
        return true;
      }

      // Show specific tool types outside the thinking box
      if (item.type === 'tool' && outsideTools.includes(item.name)) {
        return true;
      }

      return false;
    };

    const outsideItems = items.filter(isOutsideItem);
    const insideItems = items.filter((item) => !isOutsideItem(item));

    return (
      <Flex vertical>
        {/* Show ThinkingBlock if there are intermediate steps */}
        {(insideItems.length > 0 || loading) && (
          <ThinkingBlock items={insideItems} isThinking={loading} />
        )}

        {/* Show items from main agents and conversation outside */}
        {outsideItems.length > 0 && (
          <Flex vertical gap={8}>
            {outsideItems.map((item) => (
              <RenderItem
                key={item.id || `item-${item.type}-${item.name || item.text}`}
                item={item}
                onTypewriterUpdate={onTypewriterUpdate}
              />
            ))}
          </Flex>
        )}
      </Flex>
    );
  }

  // Fallback for loading state with no items
  return (
    <Flex justify="normal" style={{ width: '100%' }}>
      <Flex vertical gap={8} align="normal" style={{ width: '100%' }}>
        {loading && (
          <Flex align="center" gap={8}>
            <BeatLoader color={BluePrimary} size={6} />
            <WorkingText italic>Working...</WorkingText>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

MessageItem.propTypes = {
  items: PropTypes.arrayOf(chatItemType).isRequired,
  loading: PropTypes.bool.isRequired,
  onTypewriterUpdate: PropTypes.func,
};

export default MessageItem;
